import { useCallback, useMemo, useState, useEffect } from "react";
import { FieldJobMarks } from "./field-job-marks";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { openExternalLink } from "@/lib/standalone";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { appVersion } from "@/lib/app-version";
import { useFieldRedesign } from "@/lib/use-field-redesign";
import { useLeftOpen } from "@/components/left-open/left-open-provider";
import { LeftOpenTodayCards } from "@/components/left-open/left-open-today-card";
import { composeJobTitle, dayGreeting, originalOfficeWording, plainJobTypeName } from "./redesign-copy";
import { sortOriginCopy } from "./redesign-screens-copy";
import { useOfflineSync, queueOfflineAction } from "@/lib/offline-sync";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { PageSpinner } from "@/components/ui/spinner";
import { SyncBanner } from "@/components/sync-banner";
import { TesterLicenseNotice } from "@/components/tester-license-notice";
import { FieldTasksCard } from "@/components/field-tasks-card";
import { FieldPointsCard } from "@/components/field-points-card";
import { useDebouncedValue } from "@/components/async-picker";
import { flattenPastPages, groupByMonth, groupUpcomingByDay } from "./pastJobs";
import { cn, statusColor } from "@/lib/utils";
import { formatFieldTime } from "@/lib/datetime";
import { TestQueueTab } from "./test-queue-tab";
import { StagingTab } from "./staging-tab";
import { ReportInputGate } from "@/components/report-input-gate";
import { getTesterQueue } from "@/lib/report-input";
import {
    MapPin,
    ChevronRight,
    Clock,
    Sun,
    CalendarDays,
    History as HistoryIcon,
    Navigation,
    ArrowRight,
    Search,
    Store,
    Phone,
    AlertOctagon,
    Loader2,
    ClipboardList,
    Boxes,
    PackageCheck,
} from "lucide-react";
import { useTechSpotlightHere } from "@/teach/teach-state";
import { TeachIntro } from "@/teach/teach-card";
import { nextJobHeading, splitDay } from "./next-job";
import { PaperReadingsCard } from "./paper-readings-card";
import { withNamesFor } from "./crew-badge";

interface JobRow {
    id: string;
    title: string;
    status: string;
    customer_name: string | null;
    site_label: string | null;
    site_city: string | null;
    site_region: string | null;
    /** Site coordinates (already returned by GET /api/jobs) — used by the
     *  Supplies tab's "Closest to next job" sort. */
    latitude: number | null;
    longitude: number | null;
    trade_name: string | null;
    trade_code: string | null;
    job_type_name: string | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    updated_at?: string | null;
    /** Set by office "Ask Tech to Close Out" on the missed-jobs modal.
     *  When present, the field home shows a loud yellow banner above
     *  the job list and tapping deep-links into the job with the
     *  closeout modal already open. */
    pending_closeout_at?: string | null;
    pending_closeout_note?: string | null;
    /** Flexible crew: lead identity for the "Lead: X" badge on jobs I'm on
     *  as an added crew member. */
    assigned_to?: string | null;
    assigned_to_name?: string | null;
    /** Crew jobs (11/3): the other techs on the job, for "With: Mike" on the
     *  lead's card. GET /api/jobs already returns it. */
    crew?: Array<{ user_id: string; name: string | null; role?: string }> | null;
    /** The Quiet Toolbar (10/10) — GET /api/jobs mark state (lib/job-marks.ts). */
    parts_blocked?: boolean | null;
    fail_protocol_paused?: boolean | null;
    fail_protocol_cleared?: boolean | null;
    helper_results_to_review?: number | null;
    tech_flag_reason?: string | null;
    /** "No work order yet" (2026-10-21). */
    wo_missing?: boolean | null;
}

const TRADE_DOT: Record<string, string> = {
    backflow: "bg-blue-500",
    fire_ext: "bg-red-500",
    underground_fire: "bg-orange-500",
    plumbing: "bg-green-500",
    hvac: "bg-purple-500",
};

type TabKey = "today" | "upcoming" | "past" | "supplies" | "staging" | "test-queue";

const TABS: { key: TabKey; label: string; icon: typeof Sun }[] = [
    { key: "today", label: "Today", icon: Sun },
    { key: "upcoming", label: "Upcoming", icon: CalendarDays },
    { key: "past", label: "Past", icon: HistoryIcon },
    { key: "supplies", label: "Supplies", icon: MapPin },
    { key: "staging", label: "Parts", icon: Boxes },
    { key: "test-queue", label: "Reports", icon: ClipboardList },
];

/**
 * One subtitle per tab (#797). This was a ternary chain with cases for only
 * `today`, `upcoming` and `supplies` — so Parts and Reports both fell through
 * to the Past copy and told the tech they were looking at "Jobs you've already
 * completed." A `Record<TabKey, string>` makes that impossible: adding a tab to
 * `TabKey` without a subtitle is now a compile error rather than a silent
 * fallthrough onto whatever the last branch happened to say.
 *
 * Exported for the unit test, which asserts every TabKey has its own line and
 * that no two tabs share the Past copy.
 */
export const TAB_SUBTITLE: Record<TabKey, string> = {
    today: "Here's what's on your plate.",
    upcoming: "What's coming up next.",
    past: "Jobs you've already completed.",
    supplies: "Nearby supply houses.",
    staging: "Parts to load before you roll.",
    "test-queue": "Test reports waiting on you.",
};

function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatTime(iso: string): string {
    // Use the shared org-display-zone formatter so field screens show the same
    // wall-clock time as dispatch/office (honors VITE_FIELD_TIMEZONE).
    return formatFieldTime(iso);
}

function formatDate(iso: string): string {
    const d = new Date(iso);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (isSameDay(d, today)) return "Today";
    if (isSameDay(d, tomorrow)) return "Tomorrow";
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function tradeDot(code: string | null): string {
    return (code && TRADE_DOT[code]) || "bg-gray-400";
}

function jobAddressLine(job: JobRow): string {
    return [job.site_label, [job.site_city, job.site_region].filter(Boolean).join(", ")].filter(Boolean).join(" — ");
}

function jobMapsUrl(job: JobRow): string | null {
    const addr = jobAddressLine(job);
    if (!addr) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
}

/**
 * The ONE definition of "jobs that are this tech's" — lead or added crew.
 *
 * `include_crew=1` widens the server's `assigned_to` filter to cover crew
 * assignments too (`routes/jobsListFilter.ts`). Today's list set it and the
 * Past tab didn't, so a job a tech rode on as second man showed all day and
 * then vanished from his history the moment it completed (#1361). Both lists
 * go through here so the two can never drift apart again.
 */
export function scopeToTech(params: URLSearchParams, userId: string): URLSearchParams {
    params.set("assigned_to", userId);
    params.set("include_crew", "1");
    return params;
}

export default function FieldPage() {
    const { user } = useAuth();
    // #821 — fail-closed redesign flag; false on load/error/anything.
    const { enabled: redesignEnabled } = useFieldRedesign();
    // No job left open: an unanswered open job blocks On my way to the next one.
    const leftOpen = useLeftOpen();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { pendingCount, isOnline } = useOfflineSync();
    const [tab, setTab] = useState<TabKey>("today");
    const reportQueue = useQuery({ queryKey: ["report-input", "queue"], queryFn: getTesterQueue });
    const reportQueueCount = reportQueue.data?.items?.length ?? 0;
    const [pastSearch, setPastSearch] = useState("");

    // Helper Mode: a ride-along helper's My Jobs is the PAIRED tech's list
    // for today (day override beats default lead). They never see their own
    // (empty) schedule, and lifecycle actions stay with the tech.
    //
    // HELPER-MODE V3: a full TECH dragged onto another tech's lane on the
    // Grid gets a pairing row too — that row makes him an ACTING helper for
    // the day (rides the lead's list, helper gates apply, lead signs). Next
    // day, no row → full tech again. So the pairing query runs for everyone.
    const isHelper = user?.is_field_helper === true;
    const helperPairing = useQuery({
        queryKey: ["field", "helper-pairing", user?.id],
        queryFn: () =>
            api.get<{ date: string; pairing: { leadId: string; leadName: string } | null }>("/api/me/helper-pairing"),
        enabled: !!user?.id,
        staleTime: 5 * 60_000,
    });
    const pairedLead = helperPairing.data?.pairing ?? null;
    const actingHelper = !isHelper && !!pairedLead;
    /** Flagged helper OR full tech acting as helper today (V3). */
    const helperToday = isHelper || actingHelper;
    const jobsUserId = helperToday ? pairedLead?.leadId : user?.id;

    /** One shape for "a tech's live jobs" so the lead's day and an acting
     *  helper's own week can never drift apart (#1330). */
    const fetchActiveJobs = useCallback((assignedTo: string) => {
        const params = new URLSearchParams({
            limit: "100",
            status: "scheduled,en_route,arrived,in_progress,pending,dispatched,confirmed",
        });
        return api.get<{ jobs: JobRow[]; total: number }>(`/api/jobs?${scopeToTech(params, assignedTo)}`);
    }, []);

    const activeJobs = useQuery({
        queryKey: ["field", "jobs", jobsUserId],
        queryFn: () => fetchActiveJobs(String(jobsUserId)),
        enabled: !!jobsUserId,
    });
    // Teach cards (#1838): the Help Desk spotlight may show on this screen,
    // but never while he's at a job (arrived / working).
    useTechSpotlightHere(
        activeJobs.isSuccess &&
            !(activeJobs.data?.jobs ?? []).some((j) => j.status === "arrived" || j.status === "in_progress"),
    );

    /** #1330 — an ACTING helper is a full tech on loan for ONE day, so he
     *  keeps his own week. Today's list above is the LEAD's; his Upcoming
     *  needs its own fetch of his own jobs. A flagged helper has no
     *  schedule of his own, so this never runs for him. */
    const ownActiveJobs = useQuery({
        queryKey: ["field", "jobs", user?.id],
        queryFn: () => fetchActiveJobs(String(user?.id)),
        enabled: actingHelper && !!user?.id,
    });

    // Jobs the office has flagged for the tech to close out via the
    // missed-jobs triage modal. These are usually yesterday's jobs in
    // arrived/in_progress that need a definitive Complete /
    // Cannot-Complete / Pause decision before the day rolls over.
    // Close-out is the tech's act — helpers never see the banner.
    // Parts Custody: parts I'm holding in 'returning' state. Persistent red
    // banner (the Tristian fix) that will not clear until office checks the
    // part back in.
    const myParts = useQuery({
        queryKey: ["field", "my-parts", user?.id],
        queryFn: () =>
            api.get<{
                parts: Array<{
                    id: string;
                    short_code: string;
                    description: string;
                    unit_cost: number;
                    custody_state: string;
                }>;
            }>("/api/tracked-parts/mine"),
        enabled: !isHelper && !!user?.id,
        staleTime: 60_000,
    });
    const returningParts = (myParts.data?.parts ?? []).filter((p) => p.custody_state === "returning");

    const pendingCloseouts = useMemo(
        () => (helperToday ? [] : (activeJobs.data?.jobs ?? []).filter((j) => j.pending_closeout_at)),
        [activeJobs.data, helperToday],
    );

    // Past jobs page over a deterministic server sort (scheduled_start DESC,
    // id DESC tiebreaker) so offset pages never overlap or reshuffle. The
    // Load More button walks the offsets; flattenPastPages still de-dupes by
    // id as a belt-and-suspenders guard.
    //
    // Search is debounced before it reaches the query key so typing doesn't
    // fire a request per keystroke, and `keepPreviousData` keeps the current
    // list on screen during a key change instead of collapsing the whole tab
    // to a spinner (which read as violent layout shaking while typing).
    const PAST_PAGE_SIZE = 100;
    const debouncedPastSearch = useDebouncedValue(pastSearch, 300);
    const pastJobs = useInfiniteQuery({
        queryKey: ["field", "past-jobs", user?.id, debouncedPastSearch],
        queryFn: ({ pageParam }) => {
            const params = new URLSearchParams({
                limit: String(PAST_PAGE_SIZE),
                offset: String(pageParam),
                status: "completed,office_review,closed",
                sort: "scheduled_start",
                order: "desc",
            });
            if (user?.id) scopeToTech(params, String(user.id));
            if (debouncedPastSearch) params.set("q", debouncedPastSearch);
            return api.get<{ jobs: JobRow[]; total: number }>(`/api/jobs?${params}`);
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const fetched = allPages.reduce((n, p) => n + p.jobs.length, 0);
            return fetched < lastPage.total && lastPage.jobs.length > 0 ? fetched : undefined;
        },
        placeholderData: keepPreviousData,
        enabled: !!user?.id && tab === "past" && !isHelper,
    });

    const onMyWay = useMutation({
        mutationFn: async (jobId: string) => {
            if (!isOnline) {
                await queueOfflineAction({
                    url: `/api/jobs/${jobId}/on-my-way`,
                    method: "POST",
                    body: {},
                });
                return;
            }
            return api.post(`/api/jobs/${jobId}/on-my-way`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ["field", "jobs"] }),
        onError: (err: unknown) => {
            // #parts-gate: the server blocks the first dispatch until the tech
            // has confirmed their staged parts. Send them to the Parts tab to
            // sort it (mark Got it / flag a problem) rather than a dead error.
            if (
                err instanceof ApiError &&
                err.status === 409 &&
                (err.body as { error?: string })?.error === "PARTS_NOT_CONFIRMED"
            ) {
                toast.error((err.body as { message?: string })?.message ?? "Confirm your parts before you head out.");
                setTab("staging");
                return;
            }
            toast.error("Couldn't start the job — try again.");
        },
    });

    // #parts-gate: the tech's parts for their focus day. Shares the react-query
    // key with the Parts tab's default view, so confirming a part there clears
    // this banner instantly. Unresolved = at-shop parts not yet Got-it or flagged.
    const stageGate = useQuery({
        queryKey: ["field", "parts-to-stage", user?.id, null],
        queryFn: () =>
            api.get<{ jobs: Array<{ parts: Array<{ arrivalStatus: string; checkoffState: string | null }> }> }>(
                "/api/parts-to-stage/mine",
            ),
        enabled: !helperToday && !!user?.id,
        staleTime: 60_000,
    });
    const gateUnresolved = useMemo(() => {
        let n = 0;
        for (const j of stageGate.data?.jobs ?? [])
            for (const p of j.parts) if (p.arrivalStatus !== "on_order" && !p.checkoffState) n += 1;
        return n;
    }, [stageGate.data]);

    const now = new Date();

    const activeSplit = useMemo(() => splitDay(activeJobs.data?.jobs ?? [], now), [activeJobs.data, now]);
    const ownSplit = useMemo(() => splitDay(ownActiveJobs.data?.jobs ?? [], now), [ownActiveJobs.data, now]);

    // Today (and the next-job hero) is whoever's day I'm riding — the lead's
    // when I'm helping. Upcoming is always MINE: for everyone but an acting
    // helper `activeJobs` already is my list; for him it's the lead's, so his
    // week comes off `ownActiveJobs` (#1330).
    const { todayJobs, nextJob } = activeSplit;
    const upcomingJobs = actingHelper ? ownSplit.upcomingJobs : activeSplit.upcomingJobs;

    const restOfToday = useMemo(() => todayJobs.filter((j) => j.id !== nextJob?.id), [todayJobs, nextJob]);

    // ── "You're done for the day" → auto-surface parts to stage ──────────────
    // Techs never mark a job `completed` (finishing sends it to office_review),
    // so "finished my last job" = my active jobs for today hit zero. We remember
    // (per day, in localStorage) that there WERE jobs today so the prompt still
    // fires after the navigate-back remount — and so a plain day-off (no jobs at
    // all) never triggers it. Offline completions still show until they sync, so
    // gate on being online with nothing queued to avoid firing early.
    const stageUserKey = user?.id ?? "me";
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
        now.getDate(),
    ).padStart(2, "0")}`;
    const [stagePromptDismissed, setStagePromptDismissed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(`pts-eod-dismissed:${stageUserKey}:${todayKey}`) === "1";
        } catch {
            return false;
        }
    });
    useEffect(() => {
        if (helperToday || !activeJobs.isSuccess || todayJobs.length === 0) return;
        try {
            localStorage.setItem(`pts-had-jobs:${stageUserKey}:${todayKey}`, "1");
        } catch {
            // best-effort
        }
    }, [helperToday, activeJobs.isSuccess, todayJobs.length, stageUserKey, todayKey]);
    const hadJobsToday = (() => {
        try {
            return localStorage.getItem(`pts-had-jobs:${stageUserKey}:${todayKey}`) === "1";
        } catch {
            return false;
        }
    })();
    const dismissStagePrompt = () => {
        setStagePromptDismissed(true);
        try {
            localStorage.setItem(`pts-eod-dismissed:${stageUserKey}:${todayKey}`, "1");
        } catch {
            // best-effort
        }
    };
    const showStagePrompt =
        !helperToday &&
        activeJobs.isSuccess &&
        todayJobs.length === 0 &&
        hadJobsToday &&
        !stagePromptDismissed &&
        isOnline &&
        pendingCount === 0 &&
        tab !== "staging";

    // #parts-gate: the morning nudge to the same gate the server enforces on
    // "On My Way". Only when online — offline dispatch queues and skips the gate.
    const showPartsGate = !helperToday && isOnline && gateUnresolved > 0 && tab !== "staging";

    const upcomingByDate = useMemo(() => groupUpcomingByDay(upcomingJobs, formatDate), [upcomingJobs]);

    // De-dupe + re-sort BEFORE grouping so month headers are contiguous and
    // each group is internally ordered newest-first (§3b P0).
    const pastByMonth = useMemo(() => groupByMonth(flattenPastPages(pastJobs.data?.pages ?? [])), [pastJobs.data]);

    return (
        <div className="max-w-2xl mx-auto pb-24 space-y-4">
            <SyncBanner />
            {!isOnline && pendingCount > 0 && (
                <div className="text-center">
                    <Badge variant="warning" className="text-xs">
                        {pendingCount} offline {pendingCount === 1 ? "action" : "actions"} queued
                    </Badge>
                </div>
            )}

            {/* #1886 — amber 30 days before the backflow license runs out,
                red after (submit is refused until it's renewed). */}
            <TesterLicenseNotice where="home" />

            {returningParts.length > 0 && (
                <div className="rounded-lg border-2 border-red-500 bg-red-500/10 p-3 space-y-1">
                    <div className="flex items-center gap-2 font-semibold text-red-700 dark:text-red-300">
                        <AlertOctagon className="h-4 w-4 shrink-0" />
                        Return {returningParts.length === 1 ? "this part" : "these parts"} to the office
                    </div>
                    {returningParts.map((p) => (
                        <div key={p.id} className="text-sm text-red-700 dark:text-red-300">
                            {/* Techs never see pricing — code + description only. */}
                            Return {p.short_code} · {p.description} → the office
                        </div>
                    ))}
                    <p className="text-xs text-red-700/80 dark:text-red-300/80">
                        This banner clears when the office checks the part back in.
                    </p>
                </div>
            )}

            {pendingCloseouts.length > 0 && (
                <PendingCloseoutBanner
                    jobs={pendingCloseouts}
                    onOpen={(jobId) => navigate(`/field/jobs/${jobId}?openCloseout=1`)}
                />
            )}

            {showPartsGate && (
                <div className="space-y-2 rounded-lg border-2 border-amber-500 bg-amber-50 p-3 dark:bg-amber-900/20">
                    <div className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200">
                        <PackageCheck className="h-4 w-4 shrink-0" />
                        Confirm your parts before you head out
                    </div>
                    <p className="text-sm text-amber-900/90 dark:text-amber-200/90">
                        {gateUnresolved} part{gateUnresolved > 1 ? "s" : ""} still to sort. Tap <b>Got it</b> on each —
                        or flag a problem — then you&apos;re clear to roll.
                    </p>
                    <Button size="sm" onClick={() => setTab("staging")}>
                        Confirm parts
                    </Button>
                </div>
            )}

            {showStagePrompt && (
                <div className="space-y-2 rounded-lg border border-primary/40 bg-primary/5 p-3">
                    <div className="flex items-center gap-2 font-semibold">
                        <PackageCheck className="h-4 w-4 shrink-0 text-primary" />
                        That&apos;s a wrap for today
                    </div>
                    <p className="text-sm text-foreground/80">
                        Nice work. Review the parts to stage before your next day.
                    </p>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={() => {
                                setTab("staging");
                                dismissStagePrompt();
                            }}
                        >
                            Review parts
                        </Button>
                        <Button size="sm" variant="outline" onClick={dismissStagePrompt}>
                            Later
                        </Button>
                    </div>
                </div>
            )}

            <div className="space-y-1 -mt-1">
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground flex items-center gap-2">
                    My Jobs
                    {/* #821 — field_redesign_v2 plumbing marker. The redesign
                        screens land behind this flag in follow-up PRs; until
                        they exist, an ON flag shows this chip so the Saturday
                        pilot rehearsal can verify the whole flag path on a
                        real phone (server flag → auth-only endpoint → shell).
                        OFF (or any read failure) renders nothing — techs see
                        today's app, byte-identical. */}
                </h1>
                <p className="text-shell-foreground/80 text-sm" data-testid="field-greeting">
                    {/* #947 H1: the day opens as a SENTENCE — how many jobs,
                        where, when they start — instead of a bare tab subtitle
                        that told a new hire nothing. Only on Today (the other
                        tabs keep their own honest subtitle, #797), and only
                        behind the flag; helper-pairing copy still wins. */}
                    {helperToday ? (
                        pairedLead ? (
                            `Riding with ${pairedLead.leadName || "your tech"} today.`
                        ) : (
                            "Not paired with a tech today."
                        )
                    ) : redesignEnabled && tab === "today" ? (
                        dayGreeting(todayJobs, {
                            firstName: user?.display_name ? user.display_name.split(" ")[0] : null,
                            formatTime,
                        })
                    ) : (
                        <>
                            {user?.display_name ? `Hi ${user.display_name.split(" ")[0]} — ` : ""}
                            {TAB_SUBTITLE[tab]}
                        </>
                    )}
                </p>
            </div>

            {/* Time clock REMOVED 2026-07-15 (Corbin: "none of my techs need a
                time clock" — the 7/13 widget was an emergency exception, never
                specced). Timesheet attestations for the points gate now derive
                automatically from job activity (createPointEarnEvent), so pay
                never depends on a clock button. Hourly staff use the office
                Payroll → Time Clock surface. */}

            <div className="sticky top-0 z-10 -mx-4 px-4 sm:mx-0 sm:px-0 bg-background pt-1 pb-2 border-b border-border/60">
                <div
                    className={cn(
                        "grid gap-1 bg-secondary/60 rounded-lg p-1",
                        isHelper ? "grid-cols-2" : "grid-cols-6",
                    )}
                >
                    {/* #1330 — the collapse is for FLAGGED helpers, who have no
                        schedule of their own. An acting helper is a full tech
                        for one day: he keeps all six tabs, and every tab but
                        Today shows his OWN work. */}
                    {(isHelper ? TABS.filter((t) => t.key === "today" || t.key === "supplies") : TABS).map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={cn(
                                    "flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium transition-colors",
                                    active
                                        ? "bg-card text-foreground shadow-sm"
                                        : "text-foreground/80 hover:text-foreground",
                                )}
                            >
                                <Icon className="h-4 w-4" />
                                <span>{t.label}</span>
                                {t.key === "test-queue" && reportQueueCount > 0 && (
                                    <span className="ml-0.5 rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">
                                        {reportQueueCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* V3 acting-helper banner — a full tech paired for just today.
                Louder than the header line so the role change can't be missed. */}
            {tab === "today" && actingHelper && pairedLead && (
                <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-200">
                    <p className="font-semibold">Riding with {pairedLead.leadName || "your tech"} today</p>
                    <p className="mt-0.5">
                        You can enter everything — {pairedLead.leadName?.split(" ")[0] || "your tech"} reviews &amp;
                        signs it all. Tomorrow you&apos;re back on your own schedule.
                    </p>
                </div>
            )}

            {tab === "today" && isHelper && !pairedLead && !helperPairing.isLoading && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Sun className="h-10 w-10 mx-auto mb-3 text-foreground/80" />
                        <h3 className="font-semibold text-base mb-1">Not paired today</h3>
                        <p className="text-sm text-foreground/80">
                            You&apos;re not riding with a tech today — see the office.
                        </p>
                    </CardContent>
                </Card>
            )}

            {tab === "today" && (!helperToday || !!pairedLead) && (
                <>
                    {!helperToday && <PaperReadingsCard />}
                    {!helperToday && <LeftOpenTodayCards />}
                    {/* Teach cards (#1838): No Job Left Open explains itself once. */}
                    {!helperToday && <TeachIntro featureKey="left-open.ipad" when={leftOpen.on} />}
                    <FieldTasksCard />
                    <FieldPointsCard />
                    <TodayTab
                        isLoading={activeJobs.isLoading || (helperToday && helperPairing.isLoading)}
                        nextJob={nextJob}
                        restOfToday={restOfToday}
                        upcomingCount={isHelper ? 0 : upcomingJobs.length}
                        onOpen={(jobId) => navigate(`/field/jobs/${jobId}`)}
                        onOnMyWay={(jobId) => {
                            const next = (activeJobs.data?.jobs ?? []).find((j) => j.id === jobId);
                            const nextLabel = next ? next.site_label || next.customer_name || null : null;
                            if (leftOpen.guardOnMyWay(jobId, nextLabel)) return;
                            onMyWay.mutate(jobId);
                        }}
                        onMyWayLoading={onMyWay.isPending}
                        goUpcoming={() => setTab("upcoming")}
                        canOnMyWay={!helperToday}
                        canSeeUpcoming={!isHelper}
                        meId={user?.id ?? null}
                        redesign={redesignEnabled}
                    />
                </>
            )}

            {tab === "upcoming" && (
                <UpcomingTab
                    isLoading={actingHelper ? ownActiveJobs.isLoading : activeJobs.isLoading}
                    grouped={upcomingByDate}
                    onOpen={(jobId) => navigate(`/field/jobs/${jobId}`)}
                    meId={user?.id ?? null}
                    redesign={redesignEnabled}
                />
            )}

            {tab === "past" && (
                <PastTab
                    isLoading={pastJobs.isLoading}
                    isError={pastJobs.isError}
                    searching={pastJobs.isFetching && !pastJobs.isFetchingNextPage && !pastJobs.isLoading}
                    grouped={pastByMonth}
                    search={pastSearch}
                    onSearch={setPastSearch}
                    onOpen={(jobId) => navigate(`/field/jobs/${jobId}`)}
                    onRetry={() => void pastJobs.refetch()}
                    hasMore={pastJobs.hasNextPage === true && !pastJobs.isPlaceholderData}
                    loadingMore={pastJobs.isFetchingNextPage}
                    onLoadMore={() => void pastJobs.fetchNextPage()}
                    redesign={redesignEnabled}
                />
            )}

            {tab === "supplies" && <SupplyHousesTab nextJob={nextJob} />}

            {tab === "staging" && <StagingTab userId={user?.id ?? null} />}

            {tab === "test-queue" && <TestQueueTab />}

            <ReportInputGate />

            {/* FIELD RELIABILITY — running-build identity. Office phone
                script: "what version does your footer say?" pins the exact
                bundle a misbehaving iPad is running. Tiny on purpose. */}
            <p
                data-testid="app-version-footer"
                className="pt-6 text-center text-[10px] leading-none text-muted-foreground select-all"
            >
                {appVersion()}
            </p>
        </div>
    );
}

function TodayTab(props: {
    isLoading: boolean;
    nextJob: JobRow | null;
    restOfToday: JobRow[];
    upcomingCount: number;
    onOpen: (jobId: string) => void;
    onOnMyWay: (jobId: string) => void;
    onMyWayLoading: boolean;
    goUpcoming: () => void;
    /** False for helpers — On My Way drives job status, which is the tech's act. */
    canOnMyWay: boolean;
    /** False for flagged helpers — there is no Upcoming tab to send them to. */
    canSeeUpcoming: boolean;
    /** Flexible crew: the signed-in user, for the "Lead: X" badge. */
    meId?: string | null;
    /** #947 — field_redesign_v2. Off renders today's screen unchanged. */
    redesign?: boolean;
}) {
    if (props.isLoading) return <PageSpinner />;
    if (!props.nextJob) {
        return (
            <Card>
                <CardContent className="py-12 text-center">
                    <Sun className="h-10 w-10 mx-auto mb-3 text-foreground/80" />
                    <h3 className="font-semibold text-base mb-1">Nothing scheduled today</h3>
                    <p className="text-sm text-foreground/80 mb-4">
                        {props.canSeeUpcoming
                            ? "Enjoy the slow day — or peek at what's coming up."
                            : "Enjoy the slow day."}
                    </p>
                    {props.canSeeUpcoming && (
                        <Button variant="outline" onClick={props.goUpcoming}>
                            See upcoming
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-5">
            <NextJobHero
                job={props.nextJob}
                onOpen={() => props.onOpen(props.nextJob!.id)}
                onOnMyWay={() => props.onOnMyWay(props.nextJob!.id)}
                onMyWayLoading={props.onMyWayLoading}
                canOnMyWay={props.canOnMyWay}
                meId={props.meId}
                redesign={props.redesign}
            />

            {props.restOfToday.length > 0 && (
                <div>
                    <SectionHeader
                        icon={<Sun className="h-3.5 w-3.5" />}
                        title="Rest of today"
                        count={props.restOfToday.length}
                    />
                    <div className="space-y-2">
                        {props.restOfToday.map((j) => (
                            <CompactJobRow
                                key={j.id}
                                job={j}
                                onOpen={props.onOpen}
                                meId={props.meId}
                                redesign={props.redesign}
                            />
                        ))}
                    </div>
                </div>
            )}

            {props.upcomingCount > 0 && (
                <button
                    type="button"
                    onClick={props.goUpcoming}
                    className="w-full flex items-center justify-between gap-2 p-4 rounded-lg border border-dashed border-border bg-secondary/30 hover:bg-secondary/60 transition-colors"
                >
                    <div className="flex items-center gap-3 text-left">
                        <CalendarDays className="h-5 w-5 text-foreground/80" />
                        <div>
                            <div className="font-medium text-sm">
                                {props.upcomingCount} upcoming {props.upcomingCount === 1 ? "job" : "jobs"}
                            </div>
                            <div className="text-xs text-foreground/80">Tap to view your schedule</div>
                        </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-foreground/80 shrink-0" />
                </button>
            )}
        </div>
    );
}

export function NextJobHero({
    job,
    onOpen,
    onOnMyWay,
    onMyWayLoading,
    canOnMyWay,
    meId,
    redesign = false,
}: {
    job: JobRow;
    onOpen: () => void;
    onOnMyWay: () => void;
    onMyWayLoading: boolean;
    canOnMyWay: boolean;
    meId?: string | null;
    /** #947 H3 — plain composed title instead of the raw import string. */
    redesign?: boolean;
}) {
    const mapsUrl = jobMapsUrl(job);
    const showLeadBadge = !!meId && !!job.assigned_to && job.assigned_to !== meId;
    const withNames = withNamesFor(job, meId);
    return (
        <Card className="border-2 border-primary/30 shadow-md overflow-hidden">
            <div className="bg-primary/5 px-4 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-primary flex items-center justify-between">
                <span>{nextJobHeading(job.status)}</span>
                <Badge className={statusColor(job.status)} variant="outline">
                    {job.status?.replace(/_/g, " ")}
                </Badge>
            </div>
            <CardContent className="p-4 space-y-3">
                <div className="space-y-1">
                    <div className="font-bold text-lg leading-tight">{job.customer_name || "Customer"}</div>
                    {/* #947 H3: a green hire cannot act on "REPLACE SN: H94730".
                        Behind the flag the title COMPOSES from structured fields
                        (job type + devices); the office's original wording is
                        offered below, labeled, instead of being the headline. */}
                    <div className="text-sm text-foreground/80 leading-tight" data-testid="hero-title">
                        {redesign ? composeJobTitle(job) : job.title}
                    </div>
                    {redesign && originalOfficeWording(job) && (
                        <div className="text-xs text-foreground/55 leading-tight">
                            Note from the office: “{originalOfficeWording(job)}”
                        </div>
                    )}
                    <FieldJobMarks job={job} meId={meId} />
                    {showLeadBadge && (
                        <span
                            className="inline-flex items-center rounded-full border border-primary/50 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                            title="You're on this job as crew — the lead signs & reviews."
                        >
                            Lead: {job.assigned_to_name ?? "?"} — signs &amp; reviews
                        </span>
                    )}
                    {withNames && (
                        <span
                            className="inline-flex items-center rounded-full border border-primary/50 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                            title={`${withNames} will be with you on this job. You sign and review.`}
                            data-testid="field-with-badge"
                        >
                            With: {withNames}
                        </span>
                    )}
                </div>
                <div className="space-y-1.5 text-sm">
                    {job.scheduled_start && (
                        <div className="flex items-center gap-2 text-foreground/80">
                            <Clock className="h-4 w-4 shrink-0" />
                            <span>
                                {formatTime(job.scheduled_start)}
                                {job.scheduled_end ? ` – ${formatTime(job.scheduled_end)}` : ""}
                            </span>
                        </div>
                    )}
                    {(job.site_label || job.site_city) && (
                        <div className="flex items-start gap-2 text-foreground/80">
                            <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                            <span className="leading-snug">
                                {job.site_label}
                                {job.site_city && (
                                    <span className="block text-xs">
                                        {[job.site_city, job.site_region].filter(Boolean).join(", ")}
                                    </span>
                                )}
                            </span>
                        </div>
                    )}
                    {job.trade_name && (
                        <div className="flex items-center gap-2 text-xs text-foreground/80">
                            <span className={cn("h-2 w-2 rounded-full", tradeDot(job.trade_code))} />
                            {redesign
                                ? plainJobTypeName(job.job_type_name) || job.trade_name
                                : job.job_type_name || job.trade_name}
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-2 pt-1">
                    <Button size="lg" className="h-12 w-full text-base font-semibold" onClick={onOpen}>
                        Open Job
                        <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                        {mapsUrl && (
                            <Button
                                variant="outline"
                                size="lg"
                                className="h-11"
                                onClick={() => openExternalLink(mapsUrl)}
                            >
                                <Navigation className="h-4 w-4 mr-2" />
                                Navigate
                            </Button>
                        )}
                        {job.status === "scheduled" && canOnMyWay && (
                            <Button
                                variant="outline"
                                size="lg"
                                className={cn("h-11", !mapsUrl && "col-span-2")}
                                onClick={onOnMyWay}
                                disabled={onMyWayLoading}
                            >
                                <Navigation className="h-4 w-4 mr-2" />
                                On My Way
                            </Button>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function UpcomingTab(props: {
    isLoading: boolean;
    grouped: Array<{ key: string; label: string; jobs: JobRow[] }>;
    onOpen: (jobId: string) => void;
    /** Crew jobs (11/3): "Lead: X" / "With: Y" on upcoming days too. */
    meId?: string | null;
    /** #1059 — the office's pricing bracket doesn't belong on a tech's list. */
    redesign?: boolean;
}) {
    if (props.isLoading) return <PageSpinner />;
    const total = props.grouped.reduce((acc, g) => acc + g.jobs.length, 0);
    if (total === 0) {
        return (
            <Card>
                <CardContent className="py-12 text-center text-sm text-foreground/80">
                    <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    No upcoming jobs.
                </CardContent>
            </Card>
        );
    }
    return (
        <div className="space-y-5">
            {props.grouped.map((g) => (
                <div key={g.key}>
                    <SectionHeader
                        icon={<CalendarDays className="h-3.5 w-3.5" />}
                        title={g.label}
                        count={g.jobs.length}
                    />
                    <div className="space-y-2">
                        {g.jobs.map((j) => (
                            <CompactJobRow
                                key={j.id}
                                job={j}
                                onOpen={props.onOpen}
                                showDate={false}
                                meId={props.meId}
                                redesign={props.redesign}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function PastTab(props: {
    isLoading: boolean;
    isError: boolean;
    /** Background refetch (e.g. a debounced search) — previous rows stay rendered. */
    searching: boolean;
    grouped: Array<{ key: string; label: string; jobs: JobRow[] }>;
    search: string;
    onSearch: (s: string) => void;
    onOpen: (jobId: string) => void;
    onRetry: () => void;
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    /** #1059 — the office's pricing bracket doesn't belong on a tech's list. */
    redesign?: boolean;
}) {
    return (
        <div className="space-y-4">
            <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/80" />
                <Input
                    placeholder="Search past jobs..."
                    value={props.search}
                    onChange={(e) => props.onSearch(e.target.value)}
                    className="h-11 pl-9 pr-9"
                />
                {props.searching && (
                    <Loader2
                        aria-label="Searching"
                        className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-foreground/60"
                    />
                )}
            </div>
            {props.isLoading ? (
                <PageSpinner />
            ) : props.grouped.length === 0 && props.isError ? (
                <Card>
                    <CardContent className="py-12 text-center text-sm text-foreground/80">
                        <AlertOctagon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        <p className="mb-4">Couldn&apos;t load past jobs.</p>
                        <Button variant="outline" onClick={props.onRetry}>
                            Retry
                        </Button>
                    </CardContent>
                </Card>
            ) : props.grouped.length === 0 && props.search.trim() !== "" ? (
                /* #797 — a search that matched nothing used to render the
                   zero-history line, telling a tech who just needed different
                   keywords that they had never finished a job. Two different
                   facts; two different empty states. */
                <Card>
                    <CardContent
                        className="py-12 text-center text-sm text-foreground/80"
                        data-testid="past-search-empty"
                    >
                        <Search className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        <p className="mb-4">No jobs match &ldquo;{props.search.trim()}&rdquo; — try fewer words.</p>
                        <Button variant="outline" onClick={() => props.onSearch("")}>
                            Clear search
                        </Button>
                    </CardContent>
                </Card>
            ) : props.grouped.length === 0 ? (
                <Card>
                    <CardContent
                        className="py-12 text-center text-sm text-foreground/80"
                        data-testid="past-history-empty"
                    >
                        <HistoryIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        No completed jobs yet.
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-5">
                    {props.grouped.map((g) => (
                        <div key={g.key}>
                            <SectionHeader
                                icon={<HistoryIcon className="h-3.5 w-3.5" />}
                                title={g.label}
                                count={g.jobs.length}
                            />
                            <div className="space-y-2">
                                {g.jobs.map((j) => (
                                    <CompactJobRow
                                        key={j.id}
                                        job={j}
                                        onOpen={props.onOpen}
                                        showDate
                                        redesign={props.redesign}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                    {props.hasMore && (
                        <Button
                            variant="outline"
                            className="w-full min-h-[44px]"
                            disabled={props.loadingMore}
                            onClick={props.onLoadMore}
                        >
                            {props.loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load more"}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

function SectionHeader({ icon, title, count }: { icon: React.ReactNode; title: string; count: number }) {
    return (
        <div className="flex items-center gap-2 mb-2 px-1">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-shell-foreground/80 flex items-center gap-1.5">
                {icon}
                {title}
            </h2>
            <Badge variant="secondary" className="text-[10px]">
                {count}
            </Badge>
        </div>
    );
}

export function CompactJobRow({
    job,
    onOpen,
    showDate,
    meId,
    redesign = false,
}: {
    job: JobRow;
    onOpen: (jobId: string) => void;
    showDate?: boolean;
    /** Flexible crew: when I'm on the job but not the lead, badge who is. */
    meId?: string | null;
    /** #1059 — strips the office's pricing bracket off the job type. */
    redesign?: boolean;
}) {
    const showLeadBadge = !!meId && !!job.assigned_to && job.assigned_to !== meId;
    const withNames = withNamesFor(job, meId);
    return (
        <Card
            className="cursor-pointer hover:border-primary/50 active:scale-[0.99] transition-transform"
            onClick={() => onOpen(job.id)}
        >
            <CardContent className="p-3">
                <div className="flex items-center gap-3">
                    <div className="flex flex-col items-center justify-center w-14 shrink-0">
                        {job.scheduled_start ? (
                            <>
                                <span className="text-base font-bold leading-none">
                                    {formatTime(job.scheduled_start).replace(/\s?(AM|PM)/i, "")}
                                </span>
                                <span className="text-[10px] uppercase text-foreground/80 tracking-wider mt-0.5">
                                    {formatTime(job.scheduled_start).match(/AM|PM/i)?.[0] ?? ""}
                                </span>
                                {showDate && (
                                    <span className="text-[10px] text-foreground/80 mt-0.5">
                                        {new Date(job.scheduled_start).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-[10px] uppercase text-foreground/80 tracking-wider">no time</span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-semibold truncate">
                            <span className={cn("h-2 w-2 rounded-full shrink-0", tradeDot(job.trade_code))} />
                            <span className="truncate">{job.customer_name || "Customer"}</span>
                            {showLeadBadge && (
                                <span
                                    className="shrink-0 rounded-full border border-primary/50 bg-primary/10 px-1.5 py-0 text-[10px] font-medium text-primary"
                                    title={`${job.assigned_to_name ?? "The lead"} signs & reviews on this job`}
                                >
                                    Lead: {job.assigned_to_name ?? "?"}
                                </span>
                            )}
                            {withNames && (
                                <span
                                    className="shrink-0 rounded-full border border-primary/50 bg-primary/10 px-1.5 py-0 text-[10px] font-medium text-primary"
                                    title={`${withNames} will be with you on this job`}
                                    data-testid="field-with-badge"
                                >
                                    With: {withNames}
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-foreground/80 truncate">
                            {redesign
                                ? plainJobTypeName(job.job_type_name) || job.title
                                : job.job_type_name || job.title}
                        </div>
                        {(job.site_label || job.site_city) && (
                            <div className="text-xs text-foreground/80 truncate flex items-center gap-1 mt-0.5">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">
                                    {[job.site_label, job.site_city].filter(Boolean).join(" — ")}
                                </span>
                            </div>
                        )}
                        <div className="mt-1 empty:hidden">
                            <FieldJobMarks job={job} meId={meId} />
                        </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-foreground/80 shrink-0" />
                </div>
            </CardContent>
        </Card>
    );
}

// ─── Supply Houses Tab ────────────────────────────────────────────────

interface SupplyHouseRow {
    id: string;
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    lat: number | null;
    lng: number | null;
    phone: string | null;
    hours: string | null;
    notes: string | null;
    categories: string[];
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistance(meters: number): string {
    const miles = meters / 1609.344;
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}

/**
 * Where the tech's sort origin stands (#799). The audit found the branch list
 * running Abilene → Amarillo → Carrollton → Fort Worth → Wichita Falls with
 * "Closest to me" showing as the active control — alphabetical order wearing a
 * distance label, with branches 100–300 miles out ranked above Fort Worth ones.
 * The cause: a denied/slow/unavailable geolocation left `origin` null and the
 * sort quietly no-opped, while the control kept claiming otherwise.
 *
 * Making the origin's state explicit is what lets the UI stop lying.
 */
export type SupplyOriginState = "pending" | "ok" | "denied" | "unsupported";

/**
 * Order supply houses. With an origin: nearest first, name as the tiebreak so
 * the order is stable. WITHOUT one: an explicit A→Z, so "showing A→Z" is a
 * promise this function keeps rather than an assumption about what the server
 * happened to send (`supplyHouses.ts` does `ORDER BY name`, but the honesty of
 * the label shouldn't depend on that staying true).
 *
 * Branches with no coordinates sort last rather than jumping to the top on
 * `Infinity - Infinity = NaN`.
 */
export function sortSupplyHouses<T extends { name: string; lat: number | null; lng: number | null }>(
    items: T[],
    origin: { lat: number; lng: number } | null,
): T[] {
    const byName = (a: T, b: T) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    if (!origin) return [...items].sort(byName);
    const distance = (s: T) =>
        s.lat != null && s.lng != null ? haversineMeters(origin.lat, origin.lng, s.lat, s.lng) : Infinity;
    return [...items].sort((a, b) => {
        const dA = distance(a);
        const dB = distance(b);
        if (dA === dB) return byName(a, b);
        return dA - dB;
    });
}

/**
 * #950 SPEC P7 — a branch this far out is almost never the right answer, so
 * it dims and SAYS why rather than sitting in the list looking like a peer of
 * the one four miles away. 40 miles is roughly an hour round trip in DFW
 * traffic; the branch is still listed, still tappable, just honest about the
 * drive.
 */
const FAR_BRANCH_METERS = 40 * 1609.34;

export function farBranchNote(meters: number | null): string | null {
    if (meters == null || meters < FAR_BRANCH_METERS) return null;
    return `${Math.round(meters / 1609.34)} miles out — that's most of an hour each way.`;
}

function SupplyHousesTab({ nextJob }: { nextJob: JobRow | null }) {
    // #950 P5/P7 — presentation only. The sort itself (#799) is unchanged.
    const { enabled: redesign } = useFieldRedesign();
    const { data, isLoading } = useQuery({
        queryKey: ["supply-houses"],
        queryFn: () => api.get<{ supplyHouses: SupplyHouseRow[] }>("/api/supply-houses"),
    });

    // Auto-request location the moment the tab mounts (the tab only renders
    // when active). #799 — this used to fall through SILENTLY on a denial or a
    // timeout: the list stayed alphabetical while "Closest to me" still read as
    // active, so the tech had no way to notice they were being shown Amarillo
    // above Fort Worth. The outcome is now tracked and surfaced.
    const [myPos, setMyPos] = useState<{ lat: number; lng: number } | null>(null);
    const [geoState, setGeoState] = useState<SupplyOriginState>("pending");

    const requestLocation = useCallback(() => {
        if (!("geolocation" in navigator)) {
            setGeoState("unsupported");
            return;
        }
        setGeoState("pending");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setMyPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setGeoState("ok");
            },
            // Denial, timeout and position-unavailable all land here. They are
            // one state for the tech's purposes: we don't know where you are.
            () => setGeoState("denied"),
            { enableHighAccuracy: false, maximumAge: 300_000, timeout: 10_000 },
        );
    }, []);

    useEffect(() => {
        requestLocation();
    }, [requestLocation]);

    const [sortMode, setSortMode] = useState<"me" | "job">("me");
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState<string>("all");

    // The next job's site coords come straight off GET /api/jobs — no
    // geocoding needed at sort time.
    const jobPos =
        nextJob && nextJob.latitude != null && nextJob.longitude != null
            ? { lat: nextJob.latitude, lng: nextJob.longitude }
            : null;
    // Sort origin for the active mode; "job" quietly falls back to "me" if
    // the next job has no pin (segment is hidden in that case anyway).
    const origin = sortMode === "job" && jobPos ? jobPos : myPos;

    // Unique categories across all supply houses (case-insensitive), built
    // from the data so new admin-added categories appear automatically.
    const categories = useMemo(() => {
        const seen = new Map<string, string>();
        for (const sh of data?.supplyHouses ?? []) {
            for (const c of sh.categories ?? []) {
                const key = c.trim().toLowerCase();
                if (key && !seen.has(key)) seen.set(key, c.trim());
            }
        }
        return Array.from(seen.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [data]);

    const sorted = useMemo(() => {
        const q = search.trim().toLowerCase();
        let items = (data?.supplyHouses ?? []).filter((sh) => {
            if (category !== "all" && !(sh.categories ?? []).some((c) => c.trim().toLowerCase() === category)) {
                return false;
            }
            if (q) {
                const hay = [sh.name, sh.city, sh.address, sh.zip].filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
        // #799 — one helper for both orders, so the "A→Z" fallback is a real
        // sort rather than "whatever order the server sent". Applies AFTER the
        // category/search filter, so distances and the fallback hold under
        // every chip (the audit found HVAC opening on Longview, ~200 mi out).
        return sortSupplyHouses(items, origin);
    }, [data, origin?.lat, origin?.lng, search, category]);

    if (isLoading) return <PageSpinner />;

    if ((data?.supplyHouses ?? []).length === 0) {
        return (
            <div className="text-center py-8">
                <Store className="h-10 w-10 mx-auto text-foreground/80 mb-2" />
                <p className="text-sm text-foreground/80">No supply houses configured yet.</p>
                <p className="text-xs text-foreground/80 mt-1">Ask your admin to add supply house locations.</p>
            </div>
        );
    }

    const segClass = (selected: boolean) =>
        cn(
            "px-3 py-1.5 text-xs font-semibold rounded-md transition-colors min-h-9",
            selected ? "bg-card text-foreground shadow-sm" : "text-foreground/70 hover:text-foreground",
        );

    return (
        <div className="space-y-3">
            <div className="space-y-2">
                <div
                    className="inline-flex rounded-lg border bg-secondary p-0.5"
                    role="tablist"
                    aria-label="Sort supply houses"
                >
                    {/* #799 — "Closest to me" may only render as ACTIVE when a
                        real origin is backing it. It used to light up whenever
                        the mode was selected, regardless of whether we knew
                        where the tech was, so an alphabetical list looked
                        distance-sorted. */}
                    <button
                        type="button"
                        className={segClass((sortMode === "me" || !jobPos) && origin != null)}
                        onClick={() => setSortMode("me")}
                        data-testid="sort-closest-to-me"
                    >
                        Closest to me
                    </button>
                    {/* Hidden (not grayed out) when there's no upcoming job with a pin */}
                    {jobPos && (
                        <button
                            type="button"
                            className={segClass(sortMode === "job")}
                            onClick={() => setSortMode("job")}
                        >
                            Closest to next job
                        </button>
                    )}
                </div>
                {/* P5 — the honest description of the order, in the case that
                    used to say nothing at all: when we DO know where the tech
                    is, the label "Closest to me" is a claim, and this is the
                    claim spelled out. The no-origin case below was already
                    honest (#799). */}
                {redesign && origin != null && (
                    <p className="text-xs text-foreground/70" data-testid="supply-sort-truth">
                        {sortOriginCopy(sortMode === "job" && jobPos ? "next_job" : "me").truth}
                    </p>
                )}
                {/* #799 — say which order this actually is. Silence here was the
                    whole bug: the tech saw a plausible list and no way to tell
                    it wasn't sorted by distance. */}
                {origin == null && (
                    <div
                        className="flex flex-wrap items-center gap-2 rounded-md border border-l-4 border-l-amber-500 bg-amber-50/60 px-2.5 py-2 text-xs dark:border-l-amber-400 dark:bg-amber-500/10"
                        data-testid="supply-no-origin-notice"
                    >
                        <span className="font-semibold">
                            {geoState === "pending"
                                ? "Finding your location — showing A→Z for now"
                                : geoState === "unsupported"
                                  ? "This device won't share a location — showing A→Z"
                                  : "Can't get your location — showing A→Z"}
                        </span>
                        {geoState !== "pending" && (
                            <>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs"
                                    onClick={requestLocation}
                                    data-testid="supply-retry-location"
                                >
                                    Try again
                                </Button>
                                <span className="text-foreground/70">
                                    If you blocked location, allow it for this site in your browser settings.
                                </span>
                            </>
                        )}
                    </div>
                )}
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground/50" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by city or branch…"
                        className="pl-8 h-9 text-sm"
                    />
                </div>
                {categories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            onClick={() => setCategory("all")}
                            className={cn(
                                "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors min-h-7",
                                category === "all"
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-secondary text-secondary-foreground border-transparent hover:border-border",
                            )}
                        >
                            All
                        </button>
                        {categories.map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setCategory(key)}
                                className={cn(
                                    "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors capitalize min-h-7",
                                    category === key
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-secondary text-secondary-foreground border-transparent hover:border-border",
                                )}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {sorted.length === 0 && (
                <div className="text-center py-8">
                    <Store className="h-8 w-8 mx-auto text-foreground/60 mb-2" />
                    <p className="text-sm text-foreground/80">No supply houses match your search.</p>
                </div>
            )}
            {sorted.map((sh) => {
                const dist =
                    origin && sh.lat != null && sh.lng != null
                        ? haversineMeters(origin.lat, origin.lng, sh.lat, sh.lng)
                        : null;
                const mapsUrl =
                    sh.lat != null && sh.lng != null
                        ? `https://www.google.com/maps/search/?api=1&query=${sh.lat},${sh.lng}`
                        : sh.address
                          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([sh.address, sh.city, sh.state, sh.zip].filter(Boolean).join(", "))}`
                          : null;

                const farNote = redesign ? farBranchNote(dist) : null;

                return (
                    <Card key={sh.id} className={cn(farNote && "opacity-60")} data-testid="supply-branch-card">
                        <CardContent className="p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <Store className="h-4 w-4 text-primary shrink-0" />
                                <span className="font-semibold text-sm">{sh.name}</span>
                            </div>
                            {(sh.address || sh.city) && (
                                <div className="text-xs text-foreground/80 flex items-start gap-1.5">
                                    <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                                    {[sh.address, sh.city, sh.state, sh.zip].filter(Boolean).join(", ")}
                                </div>
                            )}
                            {dist != null && (
                                <div className="text-xs font-semibold text-primary">
                                    {formatDistance(dist)} {sortMode === "job" && jobPos ? "from next job" : "away"}
                                </div>
                            )}
                            {farNote && (
                                <div
                                    className="text-xs text-amber-700 dark:text-amber-300"
                                    data-testid="supply-far-note"
                                >
                                    {farNote}
                                </div>
                            )}
                            {sh.hours && (
                                <div className="text-xs text-foreground/80 flex items-center gap-1.5">
                                    <Clock className="h-3 w-3 shrink-0" />
                                    {sh.hours}
                                </div>
                            )}
                            {sh.categories?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {sh.categories.map((c) => (
                                        <span
                                            key={c}
                                            className="bg-secondary text-secondary-foreground text-[10px] px-2 py-0.5 rounded-full"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-2 pt-1">
                                {sh.phone && (
                                    <Button asChild variant="outline" size="sm" className="h-8 text-xs flex-1">
                                        <a href={`tel:${sh.phone.replace(/\D/g, "")}`}>
                                            <Phone className="h-3 w-3 mr-1" />
                                            Call
                                        </a>
                                    </Button>
                                )}
                                {mapsUrl && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 text-xs flex-1"
                                        onClick={() => openExternalLink(mapsUrl)}
                                    >
                                        <Navigation className="h-3 w-3 mr-1" />
                                        Navigate
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}

// ─── PendingCloseoutBanner ─────────────────────────────────────────────
//
// Shown above the field home job list whenever the office has flagged
// any of this tech's jobs with pending_closeout_at (via the missed
// jobs triage modal "Ask Tech to Close Out" action). Tapping a row
// deep-links into the job with ?openCloseout=1 so the closeout modal
// pops automatically — matching the push-notification behavior.
function PendingCloseoutBanner({ jobs, onOpen }: { jobs: JobRow[]; onOpen: (jobId: string) => void }) {
    return (
        <div className="rounded-xl border border-amber-300/70 bg-amber-50/60 dark:bg-amber-950/30 px-3 py-3 shadow-sm">
            <div className="flex items-start gap-2.5">
                <div className="rounded-full bg-amber-500/20 p-1.5 shrink-0">
                    <AlertOctagon className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                        Office needs you to close {jobs.length === 1 ? "this job" : `these ${jobs.length} jobs`} out
                    </p>
                    <p className="text-[11px] text-foreground/70 mt-0.5">
                        Tap to open and mark Complete or Cannot Complete before EOD.
                    </p>
                </div>
            </div>
            <ul className="mt-2 space-y-1.5">
                {jobs.map((j) => (
                    <li key={j.id}>
                        <button
                            type="button"
                            onClick={() => onOpen(j.id)}
                            className="w-full text-left rounded-lg border border-amber-200 dark:border-amber-500/30 bg-card hover:bg-card px-3 py-2 flex items-center justify-between gap-2 transition-colors"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-foreground truncate">
                                    {j.title || "Untitled job"}
                                </p>
                                <p className="text-[11px] text-foreground/70 truncate">
                                    {[j.customer_name, j.site_label].filter(Boolean).join(" — ")}
                                </p>
                                {j.pending_closeout_note && (
                                    <p className="text-[11px] text-amber-700 dark:text-amber-300 truncate mt-0.5">
                                        Office: {j.pending_closeout_note}
                                    </p>
                                )}
                            </div>
                            <ChevronRight className="h-4 w-4 text-foreground/50 shrink-0" />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
